import React from 'react';
import axios from 'axios';
import { AArrowUp, AArrowDown, Check, FileText, Film, Headphones, PenLine, Play, Settings, Wand2 } from 'lucide-react';
import { Icon } from './Icon';
import Button from './ui/Button';
import Input from './ui/Input';
import LanguageSelector from './LanguageSelector';
import MarkCard from './MarkCard';
import { CONFIDENCE_COLORS, ISSUE_COLORS, STATUS_LABELS } from '../constants/qa';
import { buildMp4Candidates, encodeVideoPath, getProjectRoot as getVideoProjectRoot } from '../utils/videoUtils';
import { getLanguageCode, getLanguageMeta, normalizeLanguageKey } from '../constants/languages';
import { useConfirm } from '../confirmContext';
import LoadFailure from './LoadFailure';

export default function QAPanel({ selectedVideo, selectedPart, videoRef, qaFindings, qaLoading, qaError = false, qaRunning,
                   qaFilter, setQaFilter, runQAAnalysis, runQAAllParts, refreshQA, theme, BACKEND_URL, activeSubdir = '',
                   userRole = '', availableLanguages = [],
                   // New props for three-column layout
                   marks = [], marksLoading = false, formatVTTTime, inputStyle,
                   sourceLanguage = '', vttSourceLanguage = '',
                   getProjectRoot, refreshMarks, onSwitchTape, currentUserId }) {

  const confirm = useConfirm();
  const [editingId, setEditingId] = React.useState(null);
  const [subLang, setSubLang] = React.useState('en');
  const [editText, setEditText]   = React.useState('');
  const [applyMsg, setApplyMsg]   = React.useState(null);
  const [issueTypeFilter, setIssueTypeFilter] = React.useState('all');
  const [activeFindingId, setActiveFindingId] = React.useState(null);
  const [rightTab, setRightTab] = React.useState('vtt'); // 'vtt' | 'marks'
  const [vttLines, setVttLines] = React.useState([]); // [{part, timestamp, seconds, text}]
  const [vttLoading, setVttLoading] = React.useState(false);
  const [textSize, setTextSize] = React.useState(() => {
    const saved = sessionStorage.getItem('gwv_qa_text_size');
    return saved ? Number(saved) : 4; // 1-10 scale, 4=default
  });
  const [followPlayback, setFollowPlayback] = React.useState(true);
  const [currentVttIdx, setCurrentVttIdx] = React.useState(-1);

  // Draggable column widths
  const [leftColW, setLeftColW] = React.useState(() => {
    const saved = sessionStorage.getItem('gwv_qa_left_w');
    return saved ? Number(saved) : 22;
  });
  const [rightColW, setRightColW] = React.useState(() => {
    const saved = sessionStorage.getItem('gwv_qa_right_w');
    return saved ? Number(saved) : 28;
  });
  const isDraggingRef = React.useRef(false);
  const splitContainerRef = React.useRef(null);
  const markRefsMap = React.useRef({});

  // Prompt editor state (admin only)
  const [promptOpen, setPromptOpen]       = React.useState(false);
  const [promptModel, setPromptModel]     = React.useState('');
  const [promptSystem, setPromptSystem]   = React.useState('');
  const [promptUser, setPromptUser]       = React.useState('');
  const [promptSaving, setPromptSaving]   = React.useState(false);
  const [promptMsg, setPromptMsg]         = React.useState('');
  const [promptLoaded, setPromptLoaded]   = React.useState(false);

  const loadPrompt = React.useCallback(() => {
    axios.get(`${BACKEND_URL}/qa/prompt`).then(res => {
      setPromptModel(res.data.model || '');
      setPromptSystem(res.data.system_message || '');
      setPromptUser(res.data.user_prompt || '');
      setPromptLoaded(true);
    }).catch(() => setPromptMsg('Failed to load prompt config'));
  }, [BACKEND_URL]);

  const savePrompt = async () => {
    setPromptSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/qa/prompt`, {
        model: promptModel,
        system_message: promptSystem,
        user_prompt: promptUser,
      });
      setPromptMsg('Saved');
      setTimeout(() => setPromptMsg(''), 3000);
    } catch (e) {
      setPromptMsg(`Error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPromptSaving(false);
    }
  };

  // ── Filtered findings ──
  const filtered = React.useMemo(() => {
    return qaFindings.filter(f => {
      const statusOk = qaFilter === 'all' || f.status === qaFilter;
      const typeOk = issueTypeFilter === 'all' || f.issue_type === issueTypeFilter;
      return statusOk && typeOk;
    });
  }, [qaFindings, qaFilter, issueTypeFilter]);

  const counts = React.useMemo(() => {
    const c = { all: qaFindings.length };
    ['flagged','auto_fixed','reviewed','audio_review','dismissed','archival_note'].forEach(s => {
      c[s] = qaFindings.filter(f => f.status === s).length;
    });
    return c;
  }, [qaFindings]);

  const issueCounts = React.useMemo(() => {
    const c = { all: qaFindings.length };
    Object.keys(ISSUE_COLORS).forEach(k => {
      c[k] = qaFindings.filter(f => f.issue_type === k).length;
    });
    return c;
  }, [qaFindings]);

  // ── Marks for right column ──
  const _getRoot = getProjectRoot || getVideoProjectRoot;
  const allProjectMarks = React.useMemo(() => {
    if (!marks || !selectedVideo) return [];
    const root = _getRoot(selectedVideo);
    return marks
      .filter(m => _getRoot(m.video_id) === root)
      .sort((a, b) => (a.part_number || 1) - (b.part_number || 1) || (a.adjusted_start ?? a.timestamp_start ?? 0) - (b.adjusted_start ?? b.timestamp_start ?? 0));
  }, [marks, selectedVideo, _getRoot]);

  // ── Mark lookup: map (part, seconds) → mark object for quick VTT line matching ──
  const markLookup = React.useMemo(() => {
    const map = new Map();
    for (const m of allProjectMarks) {
      const ts = Math.round((m.adjusted_start ?? m.timestamp_start ?? 0) * 10);
      const key = `${m.part_number || 1}_${ts}`;
      map.set(key, m);
    }
    return map;
  }, [allProjectMarks]);

  const findMarkForLine = React.useCallback((line) => {
    // Check within 0.5s tolerance
    for (let offset = 0; offset <= 5; offset++) {
      const ts = Math.round(line.seconds * 10);
      const key1 = `${line.part}_${ts + offset}`;
      const key2 = `${line.part}_${ts - offset}`;
      if (markLookup.has(key1)) return markLookup.get(key1);
      if (markLookup.has(key2)) return markLookup.get(key2);
    }
    return null;
  }, [markLookup]);

  // ── Follow video playback: highlight + auto-scroll current VTT line ──
  React.useEffect(() => {
    const vid = videoRef?.current;
    if (!vid || !vttLines.length || rightTab !== 'vtt') return;
    const onTimeUpdate = () => {
      const t = vid.currentTime;
      // Find the VTT line whose timestamp is closest and <= current time
      let bestIdx = -1;
      for (let i = 0; i < vttLines.length; i++) {
        if (vttLines[i].seconds <= t + 0.3) bestIdx = i;
        else break;
      }
      if (bestIdx >= 0 && bestIdx !== currentVttIdx) {
        setCurrentVttIdx(bestIdx);
        if (followPlayback && !activeFindingId) {
          const el = vttLineRefsMap.current[bestIdx];
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    };
    vid.addEventListener('timeupdate', onTimeUpdate);
    return () => vid.removeEventListener('timeupdate', onTimeUpdate);
  }, [videoRef, vttLines, rightTab, followPlayback, activeFindingId, currentVttIdx]);

  // Text size config — 10 steps
  const TEXT_SIZES = [
    { vtt: '0.50rem', en: '0.46rem' }, // 1
    { vtt: '0.58rem', en: '0.54rem' }, // 2
    { vtt: '0.64rem', en: '0.60rem' }, // 3
    { vtt: '0.70rem', en: '0.66rem' }, // 4 (default)
    { vtt: '0.78rem', en: '0.72rem' }, // 5
    { vtt: '0.86rem', en: '0.80rem' }, // 6
    { vtt: '0.96rem', en: '0.90rem' }, // 7
    { vtt: '1.08rem', en: '1.00rem' }, // 8
    { vtt: '1.22rem', en: '1.14rem' }, // 9
    { vtt: '1.40rem', en: '1.30rem' }, // 10
  ];
  const ts = TEXT_SIZES[Math.min(textSize, 10) - 1] || TEXT_SIZES[3];
  const changeTextSize = (delta) => {
    setTextSize(prev => {
      const next = Math.min(10, Math.max(1, prev + delta));
      sessionStorage.setItem('gwv_qa_text_size', String(next));
      return next;
    });
  };

  // ── Expanded MarkCard state (for VTT lines that have marks) ──
  const [expandedVttIdx, setExpandedVttIdx] = React.useState(null);

  // ── Timecode parsing ──
  const parseTimecodeSeconds = (finding) => {
    const m = (finding.swedish_source || '').match(/(\d{2}):(\d{2}):(\d{2})[.,](\d{3})/);
    if (!m) return null;
    return parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3]) + parseInt(m[4]) / 1000;
  };

  // ── Load VTT lines for all tapes (source language + English) ──
  React.useEffect(() => {
    if (!selectedVideo || !BACKEND_URL) return;
    setVttLoading(true);
    const tok = sessionStorage.getItem('gwv_token') || '';
    const parseVtt = (text) => {
      const lines = [];
      for (const block of (text || '').split(/\n\s*\n/)) {
        const blockLines = block.trim().split('\n');
        const tsLine = blockLines.find(l => l.includes('-->'));
        if (!tsLine) continue;
        const m = tsLine.match(/(\d{2}):(\d{2}):(\d{2})[.,](\d{3})/);
        if (!m) continue;
        const seconds = parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3]) + parseInt(m[4]) / 1000;
        const timestamp = `${m[1]}:${m[2]}:${m[3]}.${m[4]}`;
        const cueText = blockLines.filter(l => !l.includes('-->') && !l.match(/^\d+$/) && l.trim()).join(' ').trim();
        if (cueText) lines.push({ timestamp, seconds, text: cueText });
      }
      return lines;
    };
    const fetchVtt = async (part, lang) => {
      try {
        const langParam = lang ? `&lang=${lang}` : '';
        const url = `${BACKEND_URL}/vtt/${encodeURIComponent(selectedVideo)}?part=${part}${langParam}&token=${encodeURIComponent(tok)}`;
        const res = await axios.get(url, { responseType: 'text' });
        const text = typeof res.data === 'string' ? res.data : '';
        return text.length >= 10 ? parseVtt(text) : null;
      } catch { return null; }
    };
    (async () => {
      const allLines = [];
      let prevHash = '';
      for (let p = 1; p <= 10; p++) {
        const srcLines = await fetchVtt(p, vttSourceLanguage || null); // source language
        if (!srcLines || srcLines.length === 0) break;
        // Detect duplicate content (backend may serve same VTT for non-existent parts)
        const contentHash = srcLines.map(l => l.text).join('|').slice(0, 500);
        if (p > 1 && contentHash === prevHash) break;
        prevHash = contentHash;
        const enLines = await fetchVtt(p, 'en'); // English
        // Merge: for each source line, try to find matching English by closest timecode
        for (const line of srcLines) {
          let enText = '';
          if (enLines) {
            let bestDist = Infinity;
            for (const en of enLines) {
              const dist = Math.abs(en.seconds - line.seconds);
              if (dist < bestDist) { bestDist = dist; enText = en.text; }
            }
            if (bestDist > 2.0) enText = '';
          }
          allLines.push({ part: p, timestamp: line.timestamp, seconds: line.seconds, text: line.text, enText });
        }
      }
      setVttLines(allLines);
      setVttLoading(false);
    })();
  }, [selectedVideo, BACKEND_URL, vttSourceLanguage]);

  // ── Active VTT line index (for scroll-to on finding click) ──
  const activeVttIdx = React.useMemo(() => {
    if (!activeFindingId || rightTab !== 'vtt') return -1;
    const finding = qaFindings.find(f => f.id === activeFindingId);
    if (!finding) return -1;
    const ts = parseTimecodeSeconds(finding);
    if (ts === null) return -1;
    const findingPart = finding.part_number || selectedPart;
    let bestIdx = -1, bestDist = Infinity;
    vttLines.forEach((line, idx) => {
      if (line.part !== findingPart) return;
      const dist = Math.abs(line.seconds - ts);
      if (dist < bestDist) { bestDist = dist; bestIdx = idx; }
    });
    return bestDist < 3.0 ? bestIdx : -1;
  }, [activeFindingId, qaFindings, vttLines, selectedPart, rightTab]);

  const vttLineRefsMap = React.useRef({});

  React.useEffect(() => {
    if (activeVttIdx < 0 || rightTab !== 'vtt') return;
    const el = vttLineRefsMap.current[activeVttIdx];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeVttIdx, rightTab]);

  // ── Active mark matching (by timecode + part number) ──
  const activeMarkIdx = React.useMemo(() => {
    if (!activeFindingId) return -1;
    const finding = qaFindings.find(f => f.id === activeFindingId);
    if (!finding) return -1;
    const ts = parseTimecodeSeconds(finding);
    if (ts === null) return -1;
    const findingPart = finding.part_number || selectedPart;
    let bestIdx = -1, bestDist = Infinity;
    allProjectMarks.forEach((m, idx) => {
      if ((m.part_number || 1) !== findingPart) return;
      const dist = Math.abs((m.adjusted_start ?? m.timestamp_start) - ts);
      if (dist < bestDist) { bestDist = dist; bestIdx = idx; }
    });
    return bestDist < 2.0 ? bestIdx : -1;
  }, [activeFindingId, qaFindings, allProjectMarks, selectedPart]);

  // ── Auto-scroll right column to active mark ──
  React.useEffect(() => {
    if (activeMarkIdx < 0) return;
    const mark = allProjectMarks[activeMarkIdx];
    if (!mark) return;
    const key = mark.id || `ts-${mark.part_number}-${mark.timestamp_start}`;
    const el = markRefsMap.current[key];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeMarkIdx, allProjectMarks]);

  // ── Helpers ──
  const updateFinding = async (id, patch) => {
    try {
      await axios.put(`${BACKEND_URL}/qa/findings/${id}`, patch);
      refreshQA();
    } catch (err) {
      console.error('updateFinding failed:', err?.response?.data || err.message);
    }
  };

  const applyAll = async () => {
    // Findings are stored per tape under their own video_id (…pt2.sv, part 2)
    // and the list is fetched project-wide (part=0), so the button's enabled
    // count spans every tape. Applying only the selected video+part silently
    // dropped approved findings on all the other tapes while still reporting
    // success. Apply each tape that actually has applicable findings.
    const groups = new Map();
    (qaFindings || []).forEach(f => {
      if (f.status !== 'auto_fixed' && f.status !== 'reviewed') return;
      const vid  = f.video_id || selectedVideo;
      const part = f.part_number || selectedPart;
      groups.set(`${vid}|${part}`, { vid, part });
    });
    if (groups.size === 0) return;

    let updated = 0, created = 0, skipped = 0;
    const failures = [];
    for (const { vid, part } of groups.values()) {
      try {
        const res = await axios.post(`${BACKEND_URL}/qa/apply/${encodeURIComponent(vid)}?part=${part}`);
        updated += res.data?.updated || 0;
        created += res.data?.created || 0;
        skipped += res.data?.skipped || 0;
      } catch (err) {
        failures.push(`Tape ${part} (${err?.response?.data?.detail || err.message})`);
      }
    }

    const scope = groups.size > 1 ? ` across ${groups.size} tapes` : '';
    setApplyMsg(failures.length
      ? `Applied${scope}: ${updated} updated, ${created} created — FAILED on ${failures.join('; ')}`
      : `Applied${scope}: ${updated} updated, ${created} created, ${skipped} skipped`);
    setTimeout(() => setApplyMsg(null), 6000);
    refreshQA();
    if (refreshMarks) refreshMarks();
  };

  const seekTo = (finding) => {
    if (!videoRef?.current) return;
    const t = parseTimecodeSeconds(finding);
    if (t === null) return;
    videoRef.current.currentTime = t;
    videoRef.current.play().catch(()=>{});
  };

  // ── Save VTT line edit (creates mark if needed, then updates text) ──
  const saveVttEdit = async (line, field, newText) => {
    if (!selectedVideo || !canEdit) return;
    const svId = selectedVideo.replace(/\.en$/i, '.sv');
    const vidParts = svId.split('.');
    const baseParts = vidParts.slice(0, -1).join('.');
    // Build the correct video_id for this tape's part
    const partMatch = baseParts.match(/(.*?)(\.pt\d+|$)/i);
    const base = partMatch ? partMatch[1] : baseParts;
    const partVideoId = line.part > 1 ? `${base}.pt${line.part}.sv` : `${base}.sv`;

    try {
      // Create or get existing mark at this timestamp
      const markRes = await axios.post(`${BACKEND_URL}/mark`, {
        video_id: partVideoId,
        timestamp: line.seconds,
        part: line.part,
      });
      const markId = markRes.data?.id;
      if (!markId) return;

      if (field === 'sv') {
        // Update the main edited text
        await axios.put(`${BACKEND_URL}/marks/${markId}/text`, null, {
          params: { text: newText }
        });
      } else if (field === 'en') {
        // Update the English translation
        await axios.put(`${BACKEND_URL}/marks/${markId}/translation`, {
          language: 'en', text: newText
        });
      }
      if (refreshMarks) refreshMarks();
    } catch (err) {
      console.error('[QA] saveVttEdit failed:', err?.response?.data || err.message);
    }
  };

  const cardBg = (f) => {
    if (f.status === 'dismissed')   return theme.cardDone;
    if (f.status === 'reviewed')    return theme.card;
    if (f.status === 'auto_fixed')  return `${ISSUE_COLORS.historical_term}10`;
    if (f.status === 'audio_review') return `${theme.audioReview || '#ff9f0a'}12`;
    return theme.card;
  };

  // ── Draggable divider handlers ──
  const onLeftDividerDown = React.useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!isDraggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setLeftColW(Math.min(35, Math.max(15, pct)));
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      setLeftColW(w => { sessionStorage.setItem('gwv_qa_left_w', String(w)); return w; });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    // On touch the gesture can be cancelled (system swipe, extra finger); without
    // this the drag stays "stuck" with the col-resize cursor and text unselectable.
    document.addEventListener('pointercancel', onUp);
  }, []);

  const onRightDividerDown = React.useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!isDraggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((rect.right - ev.clientX) / rect.width) * 100;
      setRightColW(Math.min(40, Math.max(18, pct)));
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      setRightColW(w => { sessionStorage.setItem('gwv_qa_right_w', String(w)); return w; });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  }, []);

  const canEdit = userRole === 'admin' || userRole === 'editor';
  // touchAction:'none' is required for the pointer-drag resize to work on iPad —
  // without it the browser claims the touch for panning and the page scrolls
  // instead of the divider moving.
  const dividerStyle = { flex: '0 0 6px', cursor: 'col-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, touchAction: 'none' };
  const dividerDot = { width: '3px', height: '36px', borderRadius: '2px', background: theme.border };

  // ── RENDER ──
  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: 'column', minHeight: 0 }}>

      {/* QA toolbar */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'center', padding: '10px 16px',
                    background: theme.sidebar, borderBottom: `1px solid ${theme.border}`, flexShrink: 0, flexWrap: 'wrap' }}>

        <Button variant="accent" theme={theme} onClick={async () => {
          if (qaRunning) return;
          const lineCount = vttLines.length;
          const tapeCount = new Set(vttLines.map(l => l.part)).size || 1;
          const estMinutes = Math.max(2, Math.ceil(lineCount / 80));
          const proceed = await confirm({
            message:
              `Perform QA on Entire Project?\n\n` +
              `This will analyse ${tapeCount} tape${tapeCount !== 1 ? 's' : ''} with ${lineCount} subtitle blocks using AI.\n\n` +
              `Estimated time: ${estMinutes}–${estMinutes * 2} minutes\n` +
              `(longer interviews with more text take more time)\n\n` +
              `Any existing QA findings will be replaced with fresh results.\n\n` +
              `You can continue working while the analysis runs in the background.`,
            title: 'Run QA on entire project?',
            confirmLabel: 'Run QA',
            destructive: false,
          });
          if (proceed) runQAAllParts();
        }} disabled={qaRunning} loading={qaRunning} icon={Play}>
          {qaRunning ? 'ANALYSING…' : 'Perform QA on Entire Project'}
        </Button>

        <Button variant="success" theme={theme} onClick={applyAll} disabled={counts.auto_fixed + counts.reviewed === 0}>
          APPLY REVIEWED + AUTO-FIXED
        </Button>

        {applyMsg && <span style={{ fontSize: theme.fsButton, fontWeight: 'bold',
          color: applyMsg.includes('FAILED') ? (theme.danger || '#ef4444') : (theme.success || '#34c759') }}>{applyMsg}</span>}

        {userRole === 'admin' && (
          <button onClick={() => { setPromptOpen(o => !o); if (!promptLoaded) loadPrompt(); }}
            style={{ padding: '6px 12px', background: promptOpen ? theme.glossaryAccent : theme.card,
                     color: promptOpen ? '#fff' : theme.textSub, border: `1px solid ${theme.border}`,
                     borderRadius: '7px', fontWeight: 'bold', cursor: 'pointer', fontSize: theme.fsButton,
                     display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Icon icon={Settings} size={12} />PROMPT
          </button>
        )}

      </div>

      {/* Status filter tabs — flush right over VTT column */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 16px', background: theme.sidebar, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', background: theme.card, borderRadius: '8px', padding: '2px', border: `1px solid ${theme.border}`, gap: '2px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {[
            ['all',          'ALL',          null],
            ['flagged',      'FLAGGED',      null],
            ['auto_fixed',   'AUTO-FIXED',   null],
            ['reviewed',     'REVIEWED',     null],
            ['audio_review', 'AUDIO',        theme.audioReview || '#ff9f0a'],
            ['dismissed',    'DISMISSED',    null],
          ].map(([val, label, activeColor]) => (
            <button key={val} onClick={() => setQaFilter(val)}
              style={{ padding: '3px 8px', fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '0.03em', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: '0.15s', whiteSpace: 'nowrap',
                       background: qaFilter === val ? (activeColor || theme.glossaryAccent) : 'transparent',
                       color: qaFilter === val ? '#fff' : (val === 'audio_review' && counts.audio_review > 0 ? (theme.audioReview || '#ff9f0a') : theme.textSub) }}>
              {label}{counts[val] > 0 ? ` ${counts[val]}` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Admin prompt editor */}
      {promptOpen && userRole === 'admin' && (
        <div style={{ padding: '14px 20px', background: theme.sidebar, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', color: theme.textSub }}>AI PROMPT CONFIGURATION</span>
            <span style={{ display: 'inline-block', background: '#6366f1', color: '#fff', fontSize: '0.58rem', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', letterSpacing: '0.5px' }}>ADMIN</span>
            {promptMsg && <span style={{ fontSize: theme.fsBadge, color: promptMsg.startsWith('Error') ? theme.accent : (theme.success || '#34c759'), fontWeight: 'bold' }}>{promptMsg}</span>}
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', marginBottom: '3px' }}>MODEL</label>
            <Input theme={theme} value={promptModel} onChange={e => setPromptModel(e.target.value)} placeholder="claude-sonnet-4-20250514"
              style={{ maxWidth: '400px', fontFamily: 'monospace' }} />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', marginBottom: '3px' }}>SYSTEM MESSAGE</label>
            <Input multiline theme={theme} value={promptSystem} onChange={e => setPromptSystem(e.target.value)} rows={3} />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', marginBottom: '3px' }}>USER PROMPT TEMPLATE</label>
            <Input multiline size="sm" theme={theme} value={promptUser} onChange={e => setPromptUser(e.target.value)} rows={8}
              style={{ fontFamily: 'monospace' }} />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="accent" theme={theme} onClick={savePrompt} disabled={promptSaving}>
              {promptSaving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="outline" theme={theme} onClick={() => { setPromptModel(''); setPromptSystem(''); setPromptUser(''); }}>
              Reset
            </Button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          THREE-COLUMN LAYOUT: Video | Findings | Transcript
      ════════════════════════════════════════════════════════ */}
      <div ref={splitContainerRef} style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* ── LEFT: Video player ── */}
        <div style={{ width: `${leftColW}%`, minWidth: '180px', background: '#000', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', padding: '10px', gap: '8px', flexShrink: 0, overflow: 'hidden' }}>
          {availableLanguages.length > 0 && (
            <LanguageSelector
              languages={availableLanguages}
              value={subLang}
              onChange={setSubLang}
              theme={{ ...theme, inputBg: '#1a1a1a', card: '#222', sidebar: '#1a1a1a', border: '#444', text: '#ccc', textSub: '#888' }}
              compact showOff offLabel="Off" label="Subtitles"
            />
          )}
          {selectedVideo && (
            <video ref={videoRef} controls
              style={{ width: '100%', borderRadius: '8px', background: '#000' }}
              crossOrigin="anonymous"
              onLoadedData={() => {
                if (!videoRef.current) return;
                const tracks = videoRef.current.textTracks;
                for (let i = 0; i < tracks.length; i++) {
                  tracks[i].mode = (subLang !== 'off' && tracks[i].language === subLang) ? 'showing' : 'hidden';
                }
              }}>
              {buildMp4Candidates(selectedVideo).map((c,i) => <source key={i} src={encodeVideoPath(activeSubdir ?? '', c + '.mp4')} type="video/mp4" />)}
              {subLang !== 'off' && (() => {
                const parts = selectedVideo.split('.');
                const langSuffix = parts[parts.length - 1];
                const tok = sessionStorage.getItem('gwv_token') || '';
                const url = subLang === langSuffix
                  ? `${BACKEND_URL}/vtt/${encodeURIComponent(selectedVideo)}?part=${selectedPart}&token=${encodeURIComponent(tok)}`
                  : `${BACKEND_URL}/vtt/${encodeURIComponent(selectedVideo)}?part=${selectedPart}&lang=${subLang}&token=${encodeURIComponent(tok)}`;
                return <track key={subLang} label="Subtitles" kind="subtitles" src={url} srcLang={subLang} default />;
              })()}
            </video>
          )}
          <div style={{ color: '#666', fontSize: '0.6rem', textAlign: 'center', lineHeight: 1.3 }}>
            Click a finding to jump to that moment
          </div>
        </div>

        {/* Left divider */}
        <div onPointerDown={onLeftDividerDown} style={dividerStyle}
          onMouseEnter={e => { e.currentTarget.style.background = `${theme.glossaryAccent}22`; }}
          onMouseLeave={e => { if (!isDraggingRef.current) e.currentTarget.style.background = 'transparent'; }}>
          <div style={dividerDot} />
        </div>

        {/* ── CENTER: Findings ── */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Issue type filter bar */}
          <div style={{ padding: '8px 14px', borderBottom: `1px solid ${theme.border}`, background: theme.sidebar, flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {[['all', 'ALL', null], ...Object.entries(ISSUE_COLORS).map(([k, c]) => [k, k.replace(/_/g, ' ').toUpperCase(), c])].map(([val, label, color]) => {
                const active = issueTypeFilter === val;
                return (
                  <button key={val} onClick={() => setIssueTypeFilter(val)}
                    style={{
                      padding: '3px 8px', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.03em',
                      borderRadius: '999px',
                      border: `1px solid ${active ? (color || theme.glossaryAccent) : theme.border}`,
                      background: active ? `${color || theme.glossaryAccent}22` : theme.card,
                      color: active ? (color || theme.glossaryAccent) : theme.textSub,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                    }}>
                    {color && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color, flexShrink: 0 }} />}
                    {label} ({issueCounts[val] || 0})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Scrollable findings list */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {qaLoading && <div style={{ color: theme.textSub, padding: '40px', textAlign: 'center' }}>Loading findings…</div>}

            {!qaLoading && qaError && (
              <LoadFailure theme={theme} what="the QA findings" onRetry={refreshQA} />
            )}

            {!qaLoading && !qaError && filtered.length === 0 && (
              <div style={{ color: theme.textSub, padding: '40px', textAlign: 'center', lineHeight: 1.8 }}>
                {qaFindings.length === 0 ? 'No findings yet. Click "Perform QA on Entire Project" to analyse with AI.' : 'No findings match this filter.'}
              </div>
            )}

            {!qaLoading && filtered.map(f => (
              <div key={f.id}
                onClick={() => { setActiveFindingId(f.id); seekTo(f); }}
                style={{
                  background: cardBg(f), borderRadius: '10px', padding: '10px 14px',
                  border: activeFindingId === f.id
                    ? `2px solid ${theme.glossaryAccent}`
                    : f.status === 'audio_review'
                      ? `1px solid ${theme.audioReview || '#ff9f0a'}66`
                      : `1px solid ${f.status === 'dismissed' ? theme.border : (ISSUE_COLORS[f.issue_type] || theme.border)}30`,
                  opacity: f.status === 'dismissed' ? 0.55 : 1,
                  transition: '0.2s', fontSize: theme.fsCardText, cursor: 'pointer',
                }}>

                {/* Card header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  {f.part_number > 0 && (
                    <span style={{ background: theme.card, border: `1px solid ${theme.border}`, padding: '2px 7px', borderRadius: '12px', fontSize: theme.fsBadge, fontWeight: 'bold', color: theme.textSub }}>
                      <Icon icon={Film} size={9} style={{ marginRight: '2px', verticalAlign: 'middle' }} />T{f.part_number}
                    </span>
                  )}
                  <span style={{ background: (ISSUE_COLORS[f.issue_type] || '#888') + '22', color: ISSUE_COLORS[f.issue_type] || '#888',
                                 padding: '2px 8px', borderRadius: '12px', fontSize: theme.fsBadge, fontWeight: 'bold', letterSpacing: '0.04em' }}>
                    {(f.issue_type || 'unknown').replace(/_/g,' ').toUpperCase()}
                  </span>
                  <span style={{ background: (CONFIDENCE_COLORS[f.confidence] || '#888') + '22', color: CONFIDENCE_COLORS[f.confidence] || '#888',
                                 padding: '2px 8px', borderRadius: '12px', fontSize: theme.fsBadge, fontWeight: 'bold' }}>
                    {(f.confidence || '?').toUpperCase()}
                  </span>
                  {f.auto_fixable && (
                    <span style={{ background: '#007aff22', color: '#007aff', padding: '2px 8px', borderRadius: '12px', fontSize: theme.fsBadge, fontWeight: 'bold' }}>
                      <Wand2 size={9} strokeWidth={2} style={{marginRight:'2px', display:'inline', verticalAlign:'middle'}} /> AUTO
                    </span>
                  )}
                  {(f.swedish_source || '').match(/\d{2}:\d{2}:\d{2}/) && (() => {
                    const ts = ((f.swedish_source || '').match(/(\d{2}:\d{2}:\d{2}[.,]\d{3})/) || ['',''])[1];
                    return (
                      <span style={{ fontFamily: 'monospace', fontSize: theme.fsTimestamp || '0.65rem', fontWeight: 'bold', color: theme.accent, letterSpacing: '0.02em' }}>
                        {ts}
                      </span>
                    );
                  })()}
                  <span style={{ marginLeft: 'auto', fontSize: theme.fsBadge, fontWeight: 'bold',
                                 color: f.status === 'audio_review' ? (theme.audioReview || '#ff9f0a') : theme.textSub }}>
                    {STATUS_LABELS[f.status] || f.status}
                  </span>
                </div>

                {/* Analysis */}
                <div dir="auto" style={{ fontSize: '0.72rem', color: theme.textSub, marginBottom: '8px', lineHeight: 1.5, fontStyle: 'italic' }}>
                  {f.analysis}
                </div>

                {/* Translation rows */}
                {(() => {
                  const translations = f.translations || {};
                  const rows = [];
                  const snippetLength = 300;
                  const extractSnippet = (fullText) => {
                    if (!fullText || fullText.length <= snippetLength) return { text: fullText, isTruncated: false };
                    const clue = (f.location_clue || '').toLowerCase();
                    if (clue && clue.length > 5) {
                      const idx = fullText.toLowerCase().indexOf(clue);
                      if (idx >= 0) {
                        const start = Math.max(0, idx - 80);
                        const end = Math.min(fullText.length, idx + clue.length + 200);
                        return { text: (start > 0 ? '…' : '') + fullText.slice(start, end).trim() + (end < fullText.length ? '…' : ''), isTruncated: true };
                      }
                    }
                    return { text: fullText.slice(0, snippetLength).trim() + '…', isTruncated: true };
                  };

                  if (f.swedish_source) rows.push({ label: sourceLanguage || 'sv', text: f.swedish_source, isSource: true, language: sourceLanguage || 'sv' });
                  // Show English from translations or existing_english
                  const enText = translations.english || f.existing_english || '';
                  if (enText.trim()) {
                    const { text: display, isTruncated } = extractSnippet(enText);
                    rows.push({ label: 'english', text: display, fullText: enText, editable: true, language: 'english', isTruncated });
                  }
                  if (f.recommended) rows.push({ label: '_recommended', text: f.recommended, isRecommended: true });
                  const appliedText = f.applied_text || f.recommended || '';
                  if (appliedText) rows.push({ label: '_applied', text: appliedText, editable: true, language: 'applied', isApplied: true });

                  const renderPill = (lang) => {
                    const meta = getLanguageMeta(lang);
                    const code = getLanguageCode(lang);
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', padding: '1px 6px', borderRadius: '999px',
                                     border: `1px solid ${meta.border}`, background: meta.bg, color: meta.text,
                                     fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', flexShrink: 0 }}>{code}</span>
                    );
                  };

                  return rows.map(({ label, text, fullText, editable, language, isSource, isRecommended, isApplied, isTruncated }) => (
                    <div key={label} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'flex-start' }}>
                      <div style={{ width: '70px', minWidth: '70px', paddingTop: '5px', display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                        {isRecommended ? <span style={{ fontSize: '0.55rem', fontWeight: 700, color: theme.success || '#34c759' }}>REC</span>
                         : isApplied ? <span style={{ fontSize: '0.55rem', fontWeight: 700, color: theme.glossaryAccent }}>APPLIED</span>
                         : renderPill(label)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {editable && editingId === `${f.id}-${language}` ? (
                          <Input multiline autoFocus dir="auto" theme={theme} value={editText} onChange={e => setEditText(e.target.value)}
                            onBlur={() => {
                              if (language === 'applied') updateFinding(f.id, { applied_text: editText, status: 'reviewed' });
                              else updateFinding(f.id, { translations: { ...translations, [language]: editText } });
                              setEditingId(null);
                            }}
                            onClick={e => e.stopPropagation()}
                            style={{ border: `1px solid ${theme.glossaryAccent}`, fontSize: '0.72rem', minHeight: '40px' }} />
                        ) : (
                          <div dir="auto" onClick={editable ? (e) => { e.stopPropagation(); setEditingId(`${f.id}-${language}`); setEditText(language === 'applied' ? f.applied_text || f.recommended || '' : fullText || text); } : undefined}
                            style={{ fontSize: isSource ? '0.72rem' : '0.72rem', color: isSource ? theme.textSub : isRecommended ? (theme.success || '#34c759') : theme.text,
                                     fontStyle: isSource ? 'italic' : 'normal', lineHeight: 1.4, padding: '4px 6px',
                                     background: editable ? `${theme.glossaryAccent}08` : 'transparent', borderRadius: '4px',
                                     cursor: editable ? 'text' : 'default', border: editable ? `1px dashed ${theme.glossaryAccent}30` : 'none',
                                     whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {text || <span style={{ opacity: 0.4 }}>—</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  ));
                })()}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  {f.status !== 'reviewed' && (
                    <button onClick={() => updateFinding(f.id, { status: 'reviewed', applied_text: f.applied_text || f.recommended || '' })}
                      style={{ padding: '4px 10px', fontSize: theme.fsButton, fontWeight: 'bold', border: 'none', borderRadius: '6px', cursor: 'pointer', background: '#34c75922', color: '#34c759', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Icon icon={Check} size={11} />APPROVE
                    </button>
                  )}
                  {f.status !== 'audio_review' && (
                    <button onClick={() => updateFinding(f.id, { status: 'audio_review' })}
                      style={{ padding: '4px 10px', fontSize: theme.fsButton, fontWeight: 'bold', border: 'none', borderRadius: '6px', cursor: 'pointer',
                               background: `${theme.audioReview || '#ff9f0a'}22`, color: theme.audioReview || '#ff9f0a', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Icon icon={Headphones} size={11} />AUDIO
                    </button>
                  )}
                  {f.status !== 'dismissed' && (
                    <button onClick={() => updateFinding(f.id, { status: 'dismissed' })}
                      style={{ padding: '4px 10px', fontSize: theme.fsButton, fontWeight: 'bold', border: 'none', borderRadius: '6px', cursor: 'pointer', background: theme.card, color: theme.textSub }}>
                      DISMISS
                    </button>
                  )}
                  {f.status !== 'flagged' && (
                    <button onClick={() => updateFinding(f.id, { status: 'flagged' })}
                      style={{ padding: '4px 10px', fontSize: theme.fsButton, fontWeight: 'bold', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', background: 'none', color: theme.textSub }}>
                      RE-FLAG
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right divider */}
        <div onPointerDown={onRightDividerDown} style={dividerStyle}
          onMouseEnter={e => { e.currentTarget.style.background = `${theme.glossaryAccent}22`; }}
          onMouseLeave={e => { if (!isDraggingRef.current) e.currentTarget.style.background = 'transparent'; }}>
          <div style={dividerDot} />
        </div>

        {/* ── RIGHT: Transcript (tabbed: VTT lines | Marks) ── */}
        <div style={{ width: `${rightColW}%`, minWidth: '180px', borderLeft: `1px solid ${theme.border}`,
                      display: 'flex', flexDirection: 'column', overflow: 'hidden', background: theme.bg }}>
          {/* Tab header + text size controls */}
          <div style={{ padding: '6px 10px', borderBottom: `1px solid ${theme.border}`, background: theme.sidebar, flexShrink: 0,
                        display: 'flex', alignItems: 'center', gap: '4px' }}>
            {[
              { id: 'vtt', label: 'VTT', icon: FileText, count: vttLines.length },
              { id: 'marks', label: 'Marks', icon: PenLine, count: allProjectMarks.length },
            ].map(tab => (
              <button key={tab.id} onClick={() => setRightTab(tab.id)}
                style={{
                  padding: '4px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px',
                  border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                  background: rightTab === tab.id ? theme.glossaryAccent : 'transparent',
                  color: rightTab === tab.id ? '#fff' : theme.textSub,
                }}>
                <Icon icon={tab.icon} size={11} />{tab.label} ({tab.count})
              </button>
            ))}
            <span style={{ flex: 1 }} />
            {/* Text size stepper */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <button onClick={() => changeTextSize(-1)} disabled={textSize <= 1}
                title="Smaller text"
                style={{ background: 'none', border: 'none', cursor: textSize <= 1 ? 'default' : 'pointer', padding: '2px',
                         color: textSize <= 1 ? theme.border : theme.textSub, display: 'flex', opacity: textSize <= 1 ? 0.3 : 0.7 }}>
                <Icon icon={AArrowDown} size={14} />
              </button>
              <button onClick={() => changeTextSize(1)} disabled={textSize >= 10}
                title="Larger text"
                style={{ background: 'none', border: 'none', cursor: textSize >= 10 ? 'default' : 'pointer', padding: '2px',
                         color: textSize >= 10 ? theme.border : theme.textSub, display: 'flex', opacity: textSize >= 10 ? 0.3 : 0.7 }}>
                <Icon icon={AArrowUp} size={14} />
              </button>
            </div>
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px' }}>

            {/* ── VTT tab: full transcript lines ── */}
            {rightTab === 'vtt' && (
              <>
                {vttLoading && <div style={{ color: theme.textSub, padding: '20px', textAlign: 'center', fontSize: '0.75rem' }}>Loading VTT…</div>}
                {!vttLoading && vttLines.length === 0 && (
                  <div style={{ color: theme.textSub, padding: '20px', textAlign: 'center', fontSize: '0.75rem' }}>No VTT content loaded.</div>
                )}
                {!vttLoading && vttLines.map((line, idx) => {
                  const isActive = idx === activeVttIdx;
                  const isCurrent = idx === currentVttIdx && !activeFindingId;
                  const prevLine = idx > 0 ? vttLines[idx - 1] : null;
                  const showPartDivider = !prevLine || prevLine.part !== line.part;
                  const existingMark = findMarkForLine(line);
                  const isExpanded = expandedVttIdx === idx && existingMark;
                  return (
                    <React.Fragment key={idx}>
                      {showPartDivider && (
                        <div style={{ padding: '6px 8px', marginTop: idx > 0 ? '8px' : 0, marginBottom: '4px',
                                      fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em',
                                      borderBottom: `1px solid ${theme.border}`, textTransform: 'uppercase' }}>
                          <Icon icon={Film} size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Tape {line.part}
                        </div>
                      )}

                      {/* Expanded: show full MarkCard */}
                      {isExpanded ? (
                        <div ref={el => { vttLineRefsMap.current[idx] = el; }} style={{ marginBottom: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '2px' }}>
                            <button onClick={() => setExpandedVttIdx(null)}
                              style={{ background: 'none', border: 'none', fontSize: '0.58rem', color: theme.textSub, cursor: 'pointer', padding: '2px 6px' }}>
                              collapse
                            </button>
                          </div>
                          <MarkCard
                            m={existingMark}
                            editingLanguage={sourceLanguage || 'sv'}
                            visibleLanguages={availableLanguages}
                            sourceLanguage={sourceLanguage}
                            vttSourceLanguage={vttSourceLanguage}
                            theme={theme}
                            inputStyle={inputStyle || {}}
                            formatVTTTime={formatVTTTime || ((t) => t?.toFixed(3) || '0.000')}
                            BACKEND_URL={BACKEND_URL}
                            videoRef={videoRef}
                            onRefresh={refreshMarks || (() => {})}
                            showPartLabel
                            isActive={isActive}
                            canEdit={canEdit}
                            selectedPart={existingMark.part_number || selectedPart}
                            onSwitchTape={onSwitchTape || (() => {})}
                            userRole={userRole}
                            currentUserId={currentUserId}
                          />
                        </div>
                      ) : (
                        /* Lightweight VTT line card */
                        <div
                          ref={el => { vttLineRefsMap.current[idx] = el; }}
                          onClick={() => {
                            if (videoRef?.current) {
                              videoRef.current.currentTime = line.seconds;
                              videoRef.current.play().catch(() => {});
                            }
                          }}
                          style={{
                            padding: '6px 8px', borderRadius: '6px', marginBottom: '4px', cursor: 'pointer',
                            background: isActive ? `${theme.glossaryAccent}15` : isCurrent ? `${theme.accent}10` : theme.card,
                            border: isActive ? `1.5px solid ${theme.glossaryAccent}`
                              : isCurrent ? `1.5px solid ${theme.accent}55`
                              : `1px solid ${existingMark ? theme.glossaryAccent + '44' : theme.border}`,
                            transition: '0.15s',
                          }}
                          onMouseEnter={e => { if (!isActive && !isCurrent) e.currentTarget.style.borderColor = theme.glossaryAccent + '66'; }}
                          onMouseLeave={e => { if (!isActive && !isCurrent) e.currentTarget.style.borderColor = existingMark ? theme.glossaryAccent + '44' : theme.border; }}
                        >
                          {/* Timecode + badges */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: theme.accent, fontWeight: 700 }}>
                              {line.timestamp}
                            </span>
                            {existingMark && (
                              <button onClick={(e) => { e.stopPropagation(); setExpandedVttIdx(idx); }}
                                style={{ padding: '1px 6px', borderRadius: '4px', fontSize: '0.5rem', fontWeight: 700,
                                         background: `${theme.glossaryAccent}18`, color: theme.glossaryAccent,
                                         border: `1px solid ${theme.glossaryAccent}44`, cursor: 'pointer',
                                         display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                <Icon icon={PenLine} size={8} />EDITED
                              </button>
                            )}
                            {existingMark?.flagged && (
                              <span style={{ padding: '1px 6px', borderRadius: '4px', fontSize: '0.5rem', fontWeight: 700,
                                             background: '#ef444418', color: '#ef4444', border: '1px solid #ef444444' }}>
                                FLAGGED
                              </span>
                            )}
                          </div>
                          {/* Source language row */}
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', marginBottom: line.enText ? '3px' : 0 }}>
                            <span style={{ padding: '1px 5px', borderRadius: '4px', fontSize: '0.52rem', fontWeight: 700, background: '#3b82f6', color: '#fff', flexShrink: 0, marginTop: '2px' }}>
                              {getLanguageCode(sourceLanguage || 'sv')}
                            </span>
                            <span dir="auto" onClick={canEdit ? async (e) => {
                                e.stopPropagation();
                                // Create mark if needed, then expand to full MarkCard
                                if (!existingMark) {
                                  await saveVttEdit(line, 'sv', line.text);
                                }
                                setExpandedVttIdx(idx);
                              } : undefined}
                              style={{ fontSize: ts.vtt, lineHeight: 1.4, color: theme.text, cursor: canEdit ? 'text' : 'default',
                                       flex: 1, padding: '2px 4px', borderRadius: '3px',
                                       border: canEdit ? `1px dashed transparent` : 'none' }}
                              onMouseEnter={e => { if (canEdit) e.currentTarget.style.borderColor = theme.glossaryAccent + '44'; }}
                              onMouseLeave={e => { if (canEdit) e.currentTarget.style.borderColor = 'transparent'; }}
                            >{existingMark?.edited_text || line.text}</span>
                          </div>
                          {/* English row */}
                          {(line.enText || canEdit) && (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                              <span style={{ padding: '1px 5px', borderRadius: '4px', fontSize: '0.52rem', fontWeight: 700, background: '#22c55e', color: '#fff', flexShrink: 0, marginTop: '2px' }}>
                                EN
                              </span>
                              <span dir="auto" onClick={canEdit ? async (e) => {
                                  e.stopPropagation();
                                  if (!existingMark) {
                                    await saveVttEdit(line, 'sv', line.text);
                                  }
                                  setExpandedVttIdx(idx);
                                } : undefined}
                                style={{ fontSize: ts.en, lineHeight: 1.4, color: theme.textSub, cursor: canEdit ? 'text' : 'default',
                                         flex: 1, padding: '2px 4px', borderRadius: '3px',
                                         border: canEdit ? `1px dashed transparent` : 'none' }}
                                onMouseEnter={e => { if (canEdit) e.currentTarget.style.borderColor = '#22c55e44'; }}
                                onMouseLeave={e => { if (canEdit) e.currentTarget.style.borderColor = 'transparent'; }}
                              >{line.enText || <span style={{ opacity: 0.3 }}>click to edit</span>}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )}

            {/* ── Marks tab: existing editor marks ── */}
            {rightTab === 'marks' && (
              <>
                {marksLoading && <div style={{ color: theme.textSub, padding: '20px', textAlign: 'center', fontSize: '0.75rem' }}>Loading marks…</div>}
                {!marksLoading && allProjectMarks.length === 0 && (
                  <div style={{ color: theme.textSub, padding: '20px', textAlign: 'center', fontSize: '0.75rem' }}>
                    No marks yet. Open the Editor to create marks.
                  </div>
                )}
                {!marksLoading && allProjectMarks.map((m, idx) => (
                  <div key={m.id || idx} ref={el => { markRefsMap.current[m.id || `ts-${m.part_number}-${m.timestamp_start}`] = el; }}
                    style={{ marginBottom: '6px' }}>
                    <MarkCard
                      m={m}
                      editingLanguage={sourceLanguage || 'sv'}
                      visibleLanguages={availableLanguages}
                      sourceLanguage={sourceLanguage}
                      vttSourceLanguage={vttSourceLanguage}
                      theme={theme}
                      inputStyle={inputStyle || {}}
                      formatVTTTime={formatVTTTime || ((t) => t?.toFixed(3) || '0.000')}
                      BACKEND_URL={BACKEND_URL}
                      videoRef={videoRef}
                      onRefresh={refreshMarks || (() => {})}
                      showPartLabel
                      isActive={idx === activeMarkIdx}
                      canEdit={canEdit}
                      selectedPart={m.part_number || selectedPart}
                      onSwitchTape={onSwitchTape || (() => {})}
                      userRole={userRole}
                      currentUserId={currentUserId}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
